import { currentOrgKey, firstPathSegment } from '@processpuzzle/auth/domain';
import { LanguageConfig, splitLocaleFromUrl } from '@processpuzzle/util';
import { RESERVED_PATH_SEGMENTS } from './app.routes';

/**
 * The language codes the URL may be prefixed with, read from the configuration transloco is given.
 *
 * The same set `LocaleUrlSerializer` strips, derived from the same `AVAILABLE_LANGUAGES`. It has to
 * be *this* list rather than a hardcoded one: a language added to the configuration gains a URL
 * prefix immediately, and a prefix this function did not know about would be read as a tenant.
 */
export function supportedLocales(languageConfiguration?: LanguageConfig): readonly string[] {
  return (languageConfiguration?.AVAILABLE_LANGUAGES ?? []).map((language) => language.code);
}

/**
 * The tenant the URL names, or `undefined` when it names none of this application's tenants.
 *
 * **The locale prefix comes off first, and that is not a detail.** `provideLocaleRouting()` puts the
 * active language in front of every address — `/hu/signup/status/acme` — and `LocaleUrlSerializer`
 * takes it off again before the router sees it, so routes and `routerLink`s never know it is there.
 * This function reads `window.location.pathname` directly, below the router and before it, so the
 * prefix *is* there for it. Without the strip, `firstPathSegment` answers `hu`, which passes
 * `looksLikeOrgKey` as readily as any two-letter tenant would, and the realm resolves to `hu` — a
 * realm Keycloak does not have. `Keycloak.init()` then fails on the third-party-cookie probe (the
 * iframe 404s, and Keycloak serves its realm-not-found page with the *global* security headers, so
 * the browser reports a `frame-ancestors 'self'` violation rather than the missing realm), the
 * probe never answers, and authentication never initialises. Every localised URL had this, `/en`
 * included — the default language is prefixed too.
 *
 * **Then the reserved segments.** `currentOrgKey()` answers with the first path segment whenever it
 * *could* be an organization key, and `looksLikeOrgKey('signup')` is true — so without this filter
 * `/signup` would resolve the realm to `signup`, and the funnel's submit could never authenticate.
 * `/welcome` has the same bug and the same fix.
 *
 * Core deliberately does not filter reserved names inside the resolver: that list lives on the
 * backend and this code runs before the first HTTP call. The filtering belongs to the application
 * that owns the segments, which is what `RESERVED_PATH_SEGMENTS` is.
 */
export function tenantFromUrl(locales: readonly string[], pathname?: string): string | undefined {
  const url = pathname ?? (typeof window === 'undefined' ? '/' : window.location.pathname);
  const { path } = splitLocaleFromUrl(url, locales);
  const segment = firstPathSegment(path);
  // `currentOrgKey(path)`, not `currentOrgKey()` — the second would re-read the address bar and get
  // the locale back, which is the whole bug one line above it.
  return segment !== undefined && RESERVED_PATH_SEGMENTS.includes(segment) ? undefined : currentOrgKey(path);
}
